import { useState, useEffect } from "react";
import { TrainingAPI } from "../../../api/TrainingAPI";
import { Button, Space, Typography, Select, Checkbox, Card, Row, Col, Alert } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { audioMessage as message } from "../../Common/Notifications/audioMessage";
import { useSelector, useDispatch } from "react-redux";
import { setMiscConstants } from "../../../store/actions/team.actions";
import BlurredPlatformPermissions from "./blurred_platform_permissions.png";
import { PremiumFeatureDisabled } from "../../Common/PremiumFeatureDisabled";
import { AdminNotice } from "../AdminNotice";
import { useAuth0 } from "@auth0/auth0-react";

const { Title, Text } = Typography;
const { Option } = Select;

export const PlatformPermissions = ({ userPermissions }) => {
  const [permissions, setPermissions] = useState([]);
  const trainingAPI = new TrainingAPI();
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserPermissions, setSelectedUserPermissions] = useState({});
  
  const { 
    misc: {
      permission_types: permissionTypes
    },
    info: {
      platform_permissions_enabled,
      permissions_manager_list: permissionsManagerList
    }
  } = useSelector(({ team }) => team);

  const { user } = useAuth0();
  const isStaffEmail = user?.email?.endsWith("gpsdataviz.com");

  useEffect(() => {
    if (userPermissions?.PermissionsManager) {
      setPermissions(permissionTypes);
      fetchPermissionsForTeam();
    }
  }, []);
  
  const fetchPermissionsForTeam = async () => {
    try {
      const teamUsers = await trainingAPI.getPermissionsForTeamUsers();
      if (teamUsers && teamUsers.length > 0) {
        setUsers(teamUsers);
        return;
      } 
    } catch (error) {
      message.error("Error fetching users");
    }
  };

  const handleUserChange = async (userId) => {
    const user = users.find(u => u.user_id === userId);
    setSelectedUser(user);
    
    const userPerms = user.platform_permissions;
    
    if (userPerms && Object.keys(userPerms).length > 0) {
      const groupedPermissions = {};
      Object.entries(userPerms).forEach(([key, value]) => {
        const category = permissions.find(p => p.key === key)?.category;
        if (!groupedPermissions[category]) {
          groupedPermissions[category] = {};
        }
        groupedPermissions[category][key] = value;
      })
      setSelectedUserPermissions(groupedPermissions);
    } else {
      setSelectedUserPermissions(getDefaultPermissions());
    }
  };
  
  const handlePermissionChange = (category, permission, checked) => {
    setSelectedUserPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permission]: checked
      }
    }));
  };

  const getDefaultPermissions = () => {
    const defaultPermissions = {};
    permissions.forEach(permission => {
      if (!defaultPermissions[permission.category]) {
        defaultPermissions[permission.category] = {};
      }
      defaultPermissions[permission.category][permission.key] = false;
    });
    return defaultPermissions;
  }
  
  const handleSaveUserPermissions = async () => {
    try {
      if (!selectedUser) return;
      
      const flattenedPermissions = {};
      
      Object.entries(selectedUserPermissions).forEach(([category, permissions]) => {
        Object.entries(permissions).forEach(([key, value]) => {
          flattenedPermissions[key] = value;
        });
      });
      
      await trainingAPI.updateUserPermissions(selectedUser.user_id, flattenedPermissions);
      
      message.success("User permissions saved successfully");

      // Refetch permissions for current user and team
      fetchPermissionsForTeam();
      const data = await trainingAPI.getMiscellaneousConstants();
      dispatch(setMiscConstants(data))
    } catch (error) {
      message.error("Error saving user permissions");
    }
  };

  const renderPermissionCheckboxes = () => {
    if (!selectedUser) {
      return (
        <div style={{ textAlign: 'center', margin: '40px 0' }}>
          <Text type="secondary">Please select a user to manage permissions</Text>
        </div>
      );
    }

    // Group permissions by category
    const permissionsByCategory = {};
    permissions.forEach(permission => {
      if (!permissionsByCategory[permission.category]) {
        permissionsByCategory[permission.category] = [];
      }
      permissionsByCategory[permission.category].push(permission);
    });
    // Define the desired order for categories
    const categoryOrder = [
      'Permissions',
      'Settings', 
      'Metrics',
      'Data Integrity',
      'Baselines',
      'Calendar',
      'Roster',
      'Health',
      'Reports',
      'Color'
    ];

    // Sort the categories according to the desired order
    const sortedCategoryEntries = Object.entries(permissionsByCategory).sort(([categoryA], [categoryB]) => {
      const indexA = categoryOrder.indexOf(categoryA);
      const indexB = categoryOrder.indexOf(categoryB);
      
      // If both categories are in the priority list, sort by their index
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only categoryA is in the priority list, it comes first
      if (indexA !== -1 && indexB === -1) {
        return -1;
      }
      
      // If only categoryB is in the priority list, it comes first
      if (indexA === -1 && indexB !== -1) {
        return 1;
      }
      
      // If neither is in the priority list, sort alphabetically
      return categoryA.localeCompare(categoryB);
    });

    return (
      <div>
        <Row gutter={[24, 24]}>
          {sortedCategoryEntries.map(([category, categoryPermissions]) => (
            <Col span={12} key={category}>
              <Card title={`${category} Permissions`} bordered={false}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {categoryPermissions.map(permission => (
                    <Checkbox 
                      key={permission.key}
                      checked={selectedUserPermissions[category]?.[permission.key] || false}
                      onChange={(e) => handlePermissionChange(category, permission.key, e.target.checked)}
                    >
                      <div>
                        <div>{permission.key}</div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>{permission.desc}</Text>
                      </div>
                    </Checkbox>
                  ))}
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
        
        <div style={{ marginTop: 20, textAlign: 'right' }}>
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={handleSaveUserPermissions}
          >
            Save Permissions
          </Button>
        </div>
      </div>
    );
  };

  if (!platform_permissions_enabled && !isStaffEmail) {
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredPlatformPermissions}
        show_navbar={false}
        feature_name={"Platform Permissions"}
        info_text={<>
        This is a premium feature that will allow you assign permissions to users on your team. <br></br>
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    )
  }

  if (!userPermissions?.PermissionsManager) {
    return (
      <div>
        You do not have permissions to view this page. Only the team's Permissions Manager can view this page. <br/> 
        If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br/><br/>
        Your team's Permissions Manager(s): <strong>{permissionsManagerList.map(user => `${user.first_name} ${user.last_name}`).join(", ")}</strong>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">

        {
          (!platform_permissions_enabled && isStaffEmail) ? 
            <AdminNotice />
           : null
        }
        
        <div>
          <Text strong>Select User:</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Select a user to manage permissions"
            onChange={handleUserChange}
            optionFilterProp="children"
            showSearch
          >
            {users.map(user => (
              <Option key={user.user_id} value={user.user_id}>
                {user.first_name} {user.last_name} ({user.email})
              </Option>
            ))}
          </Select>
        </div>
        
        {renderPermissionCheckboxes()}

      </Space>
    </div>
  );
};